import { useCallback } from 'react';

import { useAskKlueLog } from 'hooks/dashboard';
import { AnalyticsAction } from 'lib/analytics';
import { useCreateThreadFeedbackMutation } from 'modules/shared/api/chat-feedback.api';
import {
  useAskKlueIsModernThread,
  useAskKlueThreadId,
} from 'store/views/dashboard';

type UseAskKlueFeedbackOptions = {
  isAskKlueFeedbackDialog?: boolean;
};

export type SubmitAskKlueFeedbackArgs = {
  isHelpful: boolean;
  comment?: string;
};

function useAskKlueFeedback({
  isAskKlueFeedbackDialog,
}: UseAskKlueFeedbackOptions = {}): (args: SubmitAskKlueFeedbackArgs) => void {
  const logAskKlueEvent = useAskKlueLog();
  const threadId = useAskKlueThreadId();
  const isModernThread = useAskKlueIsModernThread();
  const [createThreadFeedback] = useCreateThreadFeedbackMutation();

  return useCallback(
    ({ isHelpful, comment }: SubmitAskKlueFeedbackArgs) => {
      // comment is only sent if answer is not helpful
      const trimmedComment = isHelpful
        ? undefined
        : comment?.trim() || undefined;

      if (isModernThread) {
        if (threadId) {
          createThreadFeedback({
            threadId,
            isHelpful,
            comment: trimmedComment ?? null,
          }).catch(() => {});
        }
        return;
      }

      logAskKlueEvent({
        askKlueId: '',
        eventType: isHelpful
          ? AnalyticsAction.yesAskKlueFeedback
          : AnalyticsAction.noAskKlueFeedback,
        textFeedback: trimmedComment,
        isAskKlueFeedbackDialog,
      });
    },
    [
      createThreadFeedback,
      isAskKlueFeedbackDialog,
      isModernThread,
      logAskKlueEvent,
      threadId,
    ],
  );
}

export default useAskKlueFeedback;
